import type { UninstallShopifyIntegrationModalProps } from "@v2/types/modalTypes";

import * as React from "react";

import { Button } from "@replo/design-system/components/button/Button";
import { Modal } from "@replo/design-system/components/modal/Modal";
import { toast } from "@replo/design-system/components/toast/Toast";
import { skipToken as reactQuerySkipToken } from "@tanstack/react-query";
import { useCurrentWorkspace } from "@v2/contexts/WorkspaceDashboardContext";
import { useSubscriptionDetails } from "@v2/hooks/subscription";
import { useLogAnalytics } from "@v2/hooks/useLogAnalytics";
import {
  integrationRouter,
  integrationUtils,
} from "@v2/trpc/routes/integration";
import { projectUtils } from "@v2/trpc/routes/project";
import { docs } from "@v2/utils/docs";
import { getProjectLabels } from "@v2/utils/project-labels";
import { Info } from "lucide-react";

export const UninstallShopifyIntegrationModal: React.FC<
  UninstallShopifyIntegrationModalProps & {
    open: boolean;
    onOpenChange: (open: boolean) => void;
  }
> = ({ workspaceId, integrationId, open, onOpenChange }) => {
  const { data: shopifyIntegrationData } =
    integrationRouter.getWorkspaceShopifyIntegrationDataV2.useQuery(
      workspaceId ? { workspaceId } : reactQuerySkipToken,
    );

  const workspace = useCurrentWorkspace();
  const { data: activeSubscription } = useSubscriptionDetails();

  const analytics = useLogAnalytics();

  // NOTE (Cole 2025-11-05): Determine if this integration being removed will cancel the workspace's
  // billing plan. This happens when:
  // 1. The workspace uses Shopify billing (paymentProcessor === "shopify")
  // 2. The active subscription is managed through Shopify (paymentProcessor === "shopify")
  // 3. This is the only Shopify integration
  const isRemovingSoleShopifyIntegrationFromShopifyWorkspace =
    workspace?.paymentProcessor === "shopify" &&
    activeSubscription?.paymentProcessor === "shopify" &&
    (shopifyIntegrationData?.length ?? 0) === 1;

  const { mutate: removeShopifyIntegration, isPending: isLoading } =
    integrationRouter.deleteIntegration.useMutation({
      onSuccess: async () => {
        void projectUtils.getV2.invalidate();
        void integrationUtils.getWorkspaceShopifyIntegrationDataV2.invalidate({
          workspaceId,
        });
        toast({ title: "Shopify integration uninstalled from workspace" });
      },
      onSettled: () => {
        onOpenChange(false);
      },
    });

  const handleConfirm = () => {
    analytics("integration.shopify.remove", { workspaceId, integrationId });
    removeShopifyIntegration({ workspaceId, integrationId });
  };

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title="Uninstall Shopify Integration"
      size="base"
      footer={
        <div className="flex flex-row gap-2">
          <Button
            variant="secondary"
            size="base"
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button
            variant="destructive"
            size="base"
            type="submit"
            loading={isLoading}
            onClick={handleConfirm}
          >
            Confirm
          </Button>
        </div>
      }
    >
      <p className="text-default text-sm">
        Removing the Shopify integration from this{" "}
        {getProjectLabels("shopify").singular} will disconnect all dynamic data
        and uninstall the Replo app from your Shopify store. You won't be able
        to publish new changes until you connect this Shopify integration again.
      </p>
      {isRemovingSoleShopifyIntegrationFromShopifyWorkspace && (
        <p className="text-default inline text-sm">
          The Shopify store you are about to disconnect is responsible for the
          billing plan for this workspace. Uninstalling this Shopify Integration
          will cancel your current billing plan. If you wish to maintain a
          billing plan for this workspace that is not managed through Shopify,
          reach out to support.{" "}
          <a
            href={docs.billingPlans}
            className="text-primary inline-flex cursor-pointer items-center text-sm underline"
            target="_blank"
            rel="noreferrer"
          >
            Learn More <Info size={14} aria-hidden className="ml-1" />
          </a>
        </p>
      )}
    </Modal>
  );
};
